import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Container,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import axios from 'axios';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
}

export default function AdminReenvio(): JSX.Element {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loadingDateFilter, setLoadingDateFilter] = useState(false);

  const [cpf, setCpf] = useState('');
  const [grupo, setGrupo] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCPF, setLoadingCPF] = useState(false);
  const [loadingGrupo, setLoadingGrupo] = useState(false);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSair = () => {
    navigate('/');
  };

  const handleCloseSnackbar = () =>
    setSnackbar(prev => ({ ...prev, open: false }));

  const handleReenvioCPF = async () => {
    if (!cpf) {
      return setSnackbar({
        open: true,
        message: 'Informe um CPF.',
        severity: 'warning',
      });
    }

    const onlyDigitsCpf = cpf.replace(/\D/g, '');

    if (onlyDigitsCpf.length !== 11) {
      return setSnackbar({
        open: true,
        message: 'CPF inválido. Insira 11 números.',
        severity: 'warning',
      });
    }

    try {
      setLoadingCPF(true);

      const res = await axios.post(
        //Usar a variavel de ambiante! .env.
        `${process.env.REACT_APP_API_BASE_URL}/emails/send-mail-user?cpf=${onlyDigitsCpf}`,
        //`http://localhost:3334/emails/send-mail-user?cpf=${cpf}`,
      );

      setSnackbar({
        open: true,
        message: res.data.message || 'E-mail reenviado com sucesso!',
        severity: 'success',
      });

      setCpf('');
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erro ao reenviar e-mail.';

      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setLoadingCPF(false);
    }
  };

  const LoadingButton = ({ onClick, loading, children }: any) => (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      onClick={onClick}
      disabled={loading}
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : children}
    </Button>
  );

  const handleReenvioPorData = async () => {
    if (!startDate || !endDate) {
      return setSnackbar({
        open: true,
        message: 'Informe a data inicial e final.',
        severity: 'warning',
      });
    }

    // Validação simples para garantir que startDate <= endDate
    if (new Date(startDate) > new Date(endDate)) {
      return setSnackbar({
        open: true,
        message: 'Data inicial deve ser menor ou igual à data final.',
        severity: 'warning',
      });
    }

    try {
      setLoadingDateFilter(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/send-welcome-mails?startDate=${startDate}&endDate=${endDate}`,
      );

      setSnackbar({
        open: true,
        message:
          res.data.message || 'E-mails de boas-vindas enviados com sucesso!',
        severity: 'success',
      });

      setStartDate('');
      setEndDate('');
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Erro ao enviar e-mails de boas-vindas.',
        severity: 'error',
      });
    } finally {
      setLoadingDateFilter(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Box mt={10} p={5} border="1px solid #ccc" borderRadius={2} boxShadow={2}>
        <Typography variant="h5" gutterBottom>
          Reenvio de Boas-Vindas
        </Typography>

        <TextField
          label="CPF"
          variant="outlined"
          placeholder="99999999999"
          fullWidth
          value={cpf}
          onChange={e => {
            const rawValue = e.target.value.replace(/\D/g, ''); // Remove tudo que não for número
            const masked = rawValue
              .replace(/^(\d{3})(\d)/, '$1.$2')
              .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
              .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
              .slice(0, 14); // Garante que não passe do formato XXX.XXX.XXX-XX
            setCpf(masked);
          }}
          margin="normal"
        />

        <Button
          aria-label="Reenviar e-mail por CPF"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleReenvioCPF}
          disabled={loadingCPF}
          sx={{ mb: 4 }}
        >
          {loadingCPF ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Reenviar email por CPF'
          )}
        </Button>

        <TextField
          label="Data Inicial"
          type="date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          margin="normal"
          sx={{ backgroundColor: '#f0f0f0', borderRadius: 2, mt: 2 }}
        />
        {startDate && endDate && (
          <Box mt={2}>
            <Typography variant="body2" color="green">
              Datas preenchidas corretamente!
            </Typography>
            <Typography variant="body2">
              Início: {startDate} / Fim: {endDate}
            </Typography>
          </Box>
        )}

        <TextField
          label="Data Final"
          type="date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          margin="normal"
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleReenvioPorData}
          disabled={loadingDateFilter}
          sx={{ mb: 4 }}
        >
          {loadingDateFilter ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Reenviar Boas-Vindas por Data'
          )}
        </Button>
        <Box mt={5} p={0}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSair}
            disabled={loadingCPF || loadingGrupo}
            sx={{ mb: 4 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sair'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
